class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => { if (event.target.id === 'CartDrawer-Overlay') this.close(); });
    this.addEventListener('keydown', (event) => this.handleDrawerKeydown(event));
    this.handleCartTrigger = (event) => {
      const link = event.target.closest?.('#cart-icon-bubble');
      if (!link || (event.type === 'keydown' && ![' ', 'Enter'].includes(event.key))) return;
      if (event.type === 'click' && (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
      event.preventDefault();
      this.open(link);
    };
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('click', this.handleCartTrigger);
    document.addEventListener('keydown', this.handleCartTrigger);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleCartTrigger);
    document.removeEventListener('keydown', this.handleCartTrigger);
    cancelAnimationFrame(this.focusFrame);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    this.inert = false;
    this.removeAttribute('aria-hidden');
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.activeElement || !this.activeElement.isConnected) this.setActiveElement(document.getElementById('cart-icon-bubble'));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.renderOpenTimer);
    this.inert = false;
    this.removeAttribute('aria-hidden');
    this.classList.add('animate', 'active');
    this.restoreDrawerFocus();

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    clearTimeout(this.renderOpenTimer);
    cancelAnimationFrame(this.focusFrame);
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus();
    const trigger = this.activeElement?.isConnected && !this.contains(this.activeElement)
      ? this.activeElement : document.getElementById('cart-icon-bubble');
    trigger?.focus({ preventScroll: true });
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  restoreDrawerFocus() {
    clearTimeout(this.focusTimer);
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      const panel = this.querySelector('.drawer__inner');
      if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    });
  }

  handleDrawerKeydown(event) {
    if (event.key !== 'Tab' || !this.classList.contains('active')) return;
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;
    const elements = Array.from(getFocusableElements(panel)).filter(element => element.getClientRects().length && !element.closest('[inert]'));
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first || (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) || (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      (event.shiftKey ? last || panel : first || panel).focus({ preventScroll: true });
    }
  }

  renderContents(parsedState, { open = true } = {}) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      if (section.id === 'cart-drawer') {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('.drawer__inner');
        const panel = this.querySelector('.drawer__inner');
        if (!next || !panel) return;
        const panelScroll = panel.scrollTop;
        const focusedId = panel.contains(document.activeElement) ? document.activeElement.id : null;
        const scrollPositions = ['.bba-drawer-offer-rail', 'cart-drawer-items', '.bba-drawer-main']
          .map(selector => [selector, panel.querySelector(selector)?.scrollTop || 0]);
        this.classList.toggle('is-empty', !!doc.querySelector('cart-drawer.is-empty'));
        panel.classList.toggle('is-empty', next.classList.contains('is-empty'));
        if (panel.innerHTML === next.innerHTML) return;
        panel.innerHTML = next.innerHTML;
        panel.scrollTop = panelScroll;
        for (const [selector, top] of scrollPositions) {
          const element = panel.querySelector(selector);
          if (element) element.scrollTop = top;
        }
        const focused = focusedId ? document.getElementById(focusedId) : null;
        if (this.classList.contains('active') && focused && panel.contains(focused)) focused.focus({ preventScroll: true });
      } else {
        const element = document.getElementById(section.id);
        if (element) element.innerHTML = this.getSectionInnerHTML(html);
      }
    });
    clearTimeout(this.renderOpenTimer);
    if (this.classList.contains('active')) this.restoreDrawerFocus();
    else if (open) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


